import {
  type CreateProductBody,
  PRODUCT_STATUS_LABELS,
  Product,
  type ProductStatus,
  type VariantProfileInput,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Modal } from '../../components/Overlays';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { useCategories, useInvalidateProducts } from '../../lib/products-api';

const STATUSES = ['prototype', 'active', 'idea'] as const satisfies ProductStatus[];

/**
 * "Salvar como produto" (Quick Quote): creates the product and its first variant with the print
 * profile from the draft; the sliced file becomes the variant's first file version.
 */
export function SaveProductDialog({
  description,
  profile,
  slicedFileId,
  summary,
  onClose,
  onSaved,
}: {
  description: string;
  profile: VariantProfileInput;
  slicedFileId: string | null;
  /** "6h20 · 172 g · 1 placa" */
  summary: string;
  onClose: () => void;
  onSaved: (p: Product) => void;
}) {
  const categories = useCategories();
  const invalidate = useInvalidateProducts();
  const listId = useId();
  const [key] = useState(() => crypto.randomUUID());
  const [name, setName] = useState(description);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('prototype');
  const [variant, setVariant] = useState('Único');
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const body: CreateProductBody = {
        name: name.trim(),
        category: category.trim() || null,
        status,
        variant: { name: variant.trim(), profile, slicedFileId },
      };
      return api.post('/products', Product, body, { idempotencyKey: key });
    },
    onSuccess: (p) => {
      invalidate();
      onSaved(p);
    },
  });
  const valid = name.trim().length > 0 && variant.trim().length > 0;

  return (
    <Modal
      title="Salvar como produto"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (valid) save.mutate();
            }}
          >
            Salvar produto
          </button>
        </>
      }
    >
      <p className="sm muted">
        Vira um produto com uma variação: {summary}
        {slicedFileId ? ', com o arquivo fatiado como v1' : ''}. Dá para ajustar tudo depois em
        Produtos.
      </p>
      <div className="field">
        <label htmlFor="sp-name">Nome do produto</label>
        <input
          id="sp-name"
          className="input"
          value={name}
          aria-invalid={tried && !name.trim()}
          onChange={(e) => setName(e.target.value)}
        />
        {tried && !name.trim() ? <span className="help err">Dê um nome ao produto.</span> : null}
        <span className="help">O SKU é gerado do nome (ex.: VAS-OND).</span>
      </div>
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        <div className="field">
          <label htmlFor="sp-var">Variação</label>
          <input
            id="sp-var"
            className="input"
            placeholder="Ex.: M · Coral"
            value={variant}
            aria-invalid={tried && !variant.trim()}
            onChange={(e) => setVariant(e.target.value)}
          />
          {tried && !variant.trim() ? (
            <span className="help err">Dê um nome à variação.</span>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="sp-cat">Categoria</label>
          <input
            id="sp-cat"
            className="input"
            list={listId}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <datalist id={listId}>
            {categories.data?.data.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </datalist>
        </div>
        <div className="field">
          <label htmlFor="sp-st">Situação</label>
          <select
            id="sp-st"
            className="input"
            value={status}
            onChange={(e) => setStatus(STATUSES.find((s) => s === e.target.value) ?? 'prototype')}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {PRODUCT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {save.error ? (
        <p className="sm" style={{ color: 'var(--err)' }}>
          {errorMessage(save.error)}
        </p>
      ) : null}
    </Modal>
  );
}
